/**
 * @Description 企业微信相关工具函数
 */
import { Toast } from 'antd-mobile';
import { getWxWorkAgentConfig } from '../api/common';
import { wxWorkEnable } from './ways';
import { getCurPosition } from './amap';
import { isArray, isDef } from '@lhb/func';
import * as Sentry from '@sentry/react';

const jsApiList = [
  'checkJsApi',
  'getLocation',
];

/**
 * 初始化企业微信配置
 * @param callback 配置成功后的回调函数，参数为 wx 对象
 * @param isWxWork 是否是企业微信环境，默认 true
 * @param jsApiList 需要使用的 JS API 列表
 * @param options 其他配置选项，如 cbUrl
 */
export async function initConfig(defaultObj = {}) {
  const obj: any = {
    jsApiListMore: [],
    success() {},
    fail() {},
    cbUrl: window.location.pathname,
    ...defaultObj
  };
  if (!wxWorkEnable()) {
    obj.fail?.('非企业微信环境');
    return;
  }
  const url = window.location.href.split('#')[0];
  await window.getScript('jwxwecom');
  getWxWorkAgentConfig({ url, cbUrl: obj.cbUrl }).then((response: any) => {
    window.ww.register({
      corpId: response.data.appId, // 必填，当前用户企业所属企业ID
      jsApiList: [...obj?.jsApiListMore, ...jsApiList], // 必填，需要使用的JSAPI列表
      getConfigSignature: async () => { // 不携带jsapi_ticket的
        return {
          timestamp: response.data.timestamp, // 必填，生成签名的时间戳
          nonceStr: response.data.nonceStr, // 必填，生成签名的随机串
          signature: response.data.signature, // 必填，签名
        };
      },
      onConfigFail(err) {
        obj.fail?.('wwregister error: ' + JSON.stringify(err, null, 2));
      },
      // onConfigSuccess() {
      //   console.log(222, 'register success');
      // }
    });
    obj.success?.(window.ww);
  }, (error) => {
    const msg = error?.message || '获取企业微信配置失败';
    Toast.show(msg);
    obj.fail?.('getWxWorkAgentConfig error: ' + JSON.stringify(msg, null, 2));
  });
}

function withLocationSuccess(info) {
  const res: any = {};
  const { position, accuracy } = info || {};
  res.accuracy = accuracy;
  // 此时高德是会返回省、市、区的字段
  if (position) {
    res.longitude = position.lng;
    res.latitude = position.lat;
  }
  return res;
}

function withIpSuccess(info) {
  const res: any = {};
  const { position } = info || {};
  if (isArray(position) && position.length) { // 需要地址信息
    +(position[0]) && (res.longitude = +position[0]);
    +(position[1]) && (res.latitude = +position[1]);
  }
  res.accuracy = undefined;
  return res;
}

async function getLocation(mapIns, resolve, reject) {
  const errTip = `获取定位失败，请检查定位服务的权限是否开启(101)`;
  try {
    const info: any = await getCurPosition(mapIns, {
      // https://lbs.amap.com/api/javascript-api-v2/documentation#geolocation
      // 是否显示定位按钮
      showButton: false,
      showCircle: false,
      showMarker: false,
    }).catch(() => reject());
    // 拿到用户所在的省市信息和定位
    if (isDef(info?.accuracy)) { // 能够获取到地理定位
      resolve(withLocationSuccess(info));
    } else { // 无法获取到地理定位，就使用ip定位
      resolve(withIpSuccess(info));
    }
  } catch (error) {
    Sentry.captureException(new Error(`${errTip} ${error}`));
    Toast.show(errTip);
    reject(errTip);
  }
}

/**
 * 初始化企业微信并获取地理位置
 * @param options 配置选项
 * @returns Promise，返回位置信息 { latitude, longitude, speed, accuracy }
 */
export async function initWxWorkAndGetLocation(defaultObj = {}): Promise<{
  latitude: number;
  longitude: number;
  // speed: number;
  accuracy: number;
}> {
  const obj = {
    mapIns: undefined,
    cbUrl: undefined,
    ...defaultObj
  };

  return new Promise((resolve, reject) => {
    initConfig(
      {
        ...(obj.cbUrl ? {
          cbUrl: obj.cbUrl
        } : {}),
        success(ww) {
          ww.getLocation({
            type: 'gcj02', // 火星坐标
            success: function(res: any) {
              // console.log(111, 'getLocation success: ', res);
              resolve({
                latitude: res.latitude,
                longitude: res.longitude,
                // speed: res.speed,
                accuracy: res.accuracy,
              });
            },
            fail: function(err) {
              // console.log(111, 'getLocation fail:', err);
              if (obj.mapIns) {
                // 这里尝试走h5定位，
                // 如果还失败，就报错，提示定位失败，否则就按照h5定位的返回经纬度
                getLocation(obj.mapIns, resolve, reject);
              } else {
                const errTip = `获取定位失败，请检查定位服务的权限是否开启(102)`;
                Sentry.captureException(new Error(`${errTip} ${JSON.stringify(err, null, 2)}`));
                Toast.show(errTip);
                reject(errTip);
              }
            }
          });
        },
        fail(msg: string) {
          if (obj.mapIns) {
            // 这里尝试走h5定位，
            // 如果还失败，就报错，提示定位失败，否则就按照h5定位的返回经纬度
            getLocation(obj.mapIns, resolve, reject);
          } else {
            const errTip = `获取定位失败，请检查定位服务的权限是否开启(103)`;
            Sentry.captureException(new Error(`${errTip} ${msg}`));
            Toast.show(errTip);
            reject(errTip);
          }
        }
      }
    );
  });
}

